import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
import { logger } from '#~/logger/index.js';
import { show, showNewMessage } from '@intercom/messenger-js-sdk';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { BRIDGE_HELP_ARTICLE_URL } from './constants.js';

/**
 * Open the bridge help article. Uses ElectronAPI.openUrl so the article
 * opens in the system browser (instead of replacing the Electron window).
 * Falls back to window.open for the browser/Hive build, which has no
 * Electron shell.
 */
export const openBridgeHelpArticle = (): void => {
  if (isElectronApp()) {
    ElectronAPI.openUrl(BRIDGE_HELP_ARTICLE_URL());
    return;
  }

  if (typeof window !== 'undefined') {
    window.open(BRIDGE_HELP_ARTICLE_URL(), '_blank', 'noopener,noreferrer');
  }
};

/**
 * Open the Intercom messenger so the user can chat with support. Prefer
 * showNewMessage with prefilled context — the support team can see the
 * user reached us from the bridge-failure screen and route accordingly.
 *
 * If Intercom isn't booted (rare — `setupIntercom` is called from
 * ProfileSynchronizer well before AutoLink completes), `show` is a safe
 * no-op until the messenger boots.
 */
export const openBridgeFailureChat = (): void => {
  try {
    showNewMessage(
      `I'm trying to set up ${vendorName()} but my Bridge isn't connecting. Can you help?`
    );
  } catch (error) {
    logger.error('Failed to open Intercom from bridge failure screen', { error });

    try {
      show();
    } catch (innerError) {
      logger.error('Failed Intercom show() fallback', { error: innerError });
    }
  }
};
